import React, { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ui } from 'util/ui';
import CloseButton from './CloseButton';

/**
 * The one pop-up frame for the UI refresh (Aaron, 5 Nov 2026): dimmed page, white rounded card,
 * title with the shared close cross, then the content. Escape and a click outside close it.
 */
type Props = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose?: () => void;
  width?: number | string;
  /** Darker backdrop and no padding, for the video player. */
  media?: boolean;
  labelledBy?: string;
};

const ArchyModal: FC<Props> = ({ title, subtitle, onClose, width = 440, media = false, labelledBy = 'archy-modal-title', children }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1200,
        background: media ? 'rgba(10,12,16,0.72)' : 'rgba(22,24,29,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        boxSizing: 'border-box',
        fontFamily: ui.font,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? labelledBy : undefined}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: typeof width === 'number' ? `min(${width}px, 100%)` : width,
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          background: ui.surface,
          color: ui.ink,
          borderRadius: 16,
          boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
          overflow: 'hidden',
        }}
      >
        {(title || onClose) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 12px 12px 20px', borderBottom: `1px solid ${ui.lineSoft}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && (
                <h2 id={labelledBy} style={{ margin: 0, fontSize: 17, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {title}
                </h2>
              )}
              {subtitle && <div style={{ marginTop: 2, fontSize: 13, color: ui.muted }}>{subtitle}</div>}
            </div>
            {onClose && <CloseButton onClick={onClose} />}
          </div>
        )}
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
};

export default ArchyModal;
